import { createSelector } from "@reduxjs/toolkit";
import { RewardsAccount } from "redmond";
import { IStoreState } from "../../../../reducers/types";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.experiencesRewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) =>
  state.experiencesRewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.experiencesRewards.rewardsAccounts;

export const getIsVentureX = (state: IStoreState) =>
  getRewardsAccounts(state).some((acc) =>
    acc.productDisplayName.includes("Venture X")
  );

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.experiencesRewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.experiencesRewards.fetchRewardsAccountsCallState;

export const getRewardsAccountWithLargestValue = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount | undefined => {
    const sortedAccts = [...rewardsAccounts].sort((prev, current) => {
      return (
        current.rewardsCashEquivalent.value - prev.rewardsCashEquivalent.value
      );
    });
    return sortedAccts[0];
  }
);
export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount => rewardsAccounts[0]
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest value
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null => {
    return userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId;
  }
);

export const getSelectedAccountReferenceIdIfRedemptionEnabled = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, selectedAccountId) => {
    const account = accounts.find(
      (account) => account.accountReferenceId === selectedAccountId
    );

    return account?.allowRewardsRedemption ?? true
      ? selectedAccountId || undefined
      : undefined;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined => {
    return rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    );
  }
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) => {
    return accounts.findIndex((account) => account.accountReferenceId === id);
  }
);

export const getExperiencesShopOptionTrackingProperties = (
  state: IStoreState
) => state.experiencesShop.shopOptionTrackingProperties;
